import { baseUrl } from "util/APIBaseUrl";
import { extractErrorCode } from "util/apiError";
import { clearPlgSession, getPlgAccessToken, getPlgRefreshToken, setPlgTokens } from "plg/auth/plgTokenStore";
import { plgProofHeaders, preparePlgProof, takePreparedProof, PLG_PROOF_HEADER } from "plg/auth/deviceKey";
import { adjustClockFromServer, serverTimeFrom } from "plg/auth/clockOffset";
import { resetPlgAnalytics } from "plg/analytics/identity";
import { PLG_SIGNUP_ROUTE } from "constants/routes";

export interface PlgResponse<T> {
  ok: boolean;
  status: number;
  payload: T;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export const PLG_EXPIRED_REDIRECT = `${PLG_SIGNUP_ROUTE}?expired=1`;
const PROOF_INVALID = "PLG_PROOF_INVALID";

interface Sent {
  res: Response;
  payload: unknown;
}

const isProofInvalid = (s: Sent) => s.res.status === 401 && extractErrorCode(s.payload) === PROOF_INVALID;

/**
 * Sends once; when the server rejects the proof (usually a wrong device clock), learns the
 * server time from the error and sends once more with a fresh proof (v2.2).
 */
async function withClockRetry(send: () => Promise<Sent>): Promise<Sent> {
  const first = await send();
  if (!isProofInvalid(first)) return first;
  adjustClockFromServer(serverTimeFrom(first.payload));
  return send();
}

type RefreshOutcome = "refreshed" | "failed" | "proof";

async function postRefresh(refreshToken: string): Promise<Sent> {
  const url = `${baseUrl}/v1/plg/auth/refresh`;
  return withClockRetry(async () => {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await plgProofHeaders("POST", url)) },
      body: JSON.stringify({ refreshToken }),
    });
    return { res, payload: await res.json().catch(() => null) };
  });
}

/** Tabs share one session, so refreshes are serialised across tabs when the browser supports it. */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== "undefined" ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
  return locks?.request ? (locks.request("plg-refresh", fn) as Promise<T>) : fn();
}

let refreshing: Promise<RefreshOutcome> | null = null;

/** `seenAccess` is the access token the failed request used; if another tab already replaced it, just use the new one. */
function refreshOnce(seenAccess: string | null): Promise<RefreshOutcome> {
  if (!refreshing) {
    refreshing = withRefreshLock(async (): Promise<RefreshOutcome> => {
      const current = getPlgAccessToken();
      if (current && current !== seenAccess) return "refreshed";
      const refreshToken = getPlgRefreshToken();
      if (!refreshToken) return "failed";
      try {
        const sent = await postRefresh(refreshToken);
        if (sent.res.ok) return setPlgTokens(sent.payload as never) !== null ? "refreshed" : "failed";
        if (isProofInvalid(sent)) return "proof";
        // Without cross-tab locks another tab may have rotated the token meanwhile.
        return getPlgRefreshToken() !== refreshToken ? "refreshed" : "failed";
      } catch {
        return "failed";
      }
    }).finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

/** Full-page navigation, as an object so tests can replace it (jsdom's location can't be redefined). */
export const plgNavigation = {
  go: (url: string) => window.location.assign(url),
  /** Leaves without a history entry, so Back cannot return to the page being left. */
  replace: (url: string) => window.location.replace(url),
};

export function endPlgSession(redirect: string = PLG_EXPIRED_REDIRECT): void {
  clearPlgSession();
  resetPlgAnalytics();
  plgNavigation.go(redirect);
}

/** An account Edge suspended or blocked, or one moved to the portal, ends the PLG session with a reason the sign-in card explains. */
const ACCOUNT_END_REASONS: Record<string, string> = {
  ACCOUNT_SUSPENDED: `${PLG_SIGNUP_ROUTE}?reason=suspended`,
  ACCOUNT_BLOCKED: `${PLG_SIGNUP_ROUTE}?reason=blocked`,
  ACCOUNT_CONVERTED: `${PLG_SIGNUP_ROUTE}?reason=converted`,
  COUNTRY_NOT_SUPPORTED: `${PLG_SIGNUP_ROUTE}?reason=country`,
};

/** Sign a proof for a request that will be sent while the page unloads (see plgRequest keepalive). */
export function warmPlgProof(method: Method, path: string): void {
  preparePlgProof(method, `${baseUrl}${path}`, getPlgAccessToken());
}

export async function plgRequest<T = unknown>(
  method: Method,
  path: string,
  body?: unknown,
  opts: { blob?: boolean; keepalive?: boolean } = {}
): Promise<PlgResponse<T>> {
  const url = `${baseUrl}${path}`;
  // A FormData body (file upload) goes as it is: the browser sets the multipart Content-Type.
  const multipart = typeof FormData !== "undefined" && body instanceof FormData;
  const baseHeaders = (token: string | null) => ({
    Authorization: `Bearer ${token || ""}`,
    ...(body !== undefined && !multipart ? { "Content-Type": "application/json" } : {}),
  });
  const parse = async (res: Response) => (opts.blob && res.ok ? res.blob() : res.json().catch(() => null));

  // A fresh device proof (bound to the access token via `ath`) on every attempt, including retries.
  const send = async (): Promise<Sent> => {
    const token = getPlgAccessToken();
    // During page unload there is no time for WebCrypto: use the proof signed in advance, synchronously.
    const ready = opts.keepalive ? takePreparedProof(method, url, token) : null;
    const proof = ready ? { [PLG_PROOF_HEADER]: ready } : await plgProofHeaders(method, url, { accessToken: token });
    const res = await fetch(url, {
      method,
      keepalive: opts.keepalive,
      headers: { ...baseHeaders(token), ...proof },
      body: multipart ? (body as FormData) : body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (opts.keepalive) preparePlgProof(method, url, getPlgAccessToken());
    return { res, payload: await parse(res) };
  };

  const proofFailure = (s: Sent): PlgResponse<T> => ({ ok: false, status: 401, payload: s.payload as T });

  const seenAccess = getPlgAccessToken();
  let sent = await withClockRetry(send);
  // A proof the server still can't accept is not a dead session: report it, keep the tokens.
  if (isProofInvalid(sent)) return proofFailure(sent);
  if (sent.res.status === 401) {
    const outcome = await refreshOnce(seenAccess);
    if (outcome === "proof") return proofFailure(sent);
    if (outcome === "failed") {
      endPlgSession();
      return { ok: false, status: 401, payload: null as T };
    }
    sent = await withClockRetry(send);
    if (isProofInvalid(sent)) return proofFailure(sent);
    if (sent.res.status === 401) {
      endPlgSession();
      return { ok: false, status: 401, payload: null as T };
    }
  }
  const { res, payload } = sent;
  if (res.status === 403 || res.status === 409) {
    const reason = ACCOUNT_END_REASONS[extractErrorCode(payload) || ""];
    if (reason) endPlgSession(reason);
  }
  return { ok: res.ok, status: res.status, payload: payload as T };
}
